import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import liteIcon from "../assets/icons/LiteIcon.png";
import { API_URL } from "../config";
import {
  FaArrowLeft,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaCheckCircle,
} from "react-icons/fa";
import { CircularProgress } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";

const Ring = ({
  size,
  color,
  top,
  left,
  right,
  bottom,
  delay = 0,
  duration = 10,
  dashed,
}) => (
  <motion.div
    style={{
      position: "absolute",
      width: size,
      height: size,
      borderRadius: "50%",
      border: `${dashed ? "2px dashed" : "1.5px solid"} ${color}`,
      top,
      left,
      right,
      bottom,
      pointerEvents: "none",
    }}
    animate={{ rotate: 360 }}
    transition={{ duration, delay, repeat: Infinity, ease: "linear" }}
  />
);

const Pulse = ({ style, delay = 0, color = "rgba(255,255,255,0.5)" }) => (
  <motion.div
    style={{
      width: 9,
      height: 9,
      borderRadius: "50%",
      background: color,
      position: "absolute",
      ...style,
    }}
    animate={{ scale: [1, 2.2, 1], opacity: [0.7, 0, 0.7] }}
    transition={{ duration: 3.2, delay, repeat: Infinity, ease: "easeInOut" }}
  />
);

const FloatingIcon = ({ emoji, style, delay = 0, duration = 6 }) => (
  <motion.div
    style={{
      position: "absolute",
      fontSize: "clamp(22px, 2.8vw, 34px)",
      filter: "drop-shadow(0 6px 16px rgba(0,0,0,0.2))",
      userSelect: "none",
      zIndex: 2,
      ...style,
    }}
    animate={{ y: [0, -18, 0], rotate: [-4, 4, -4], scale: [1, 1.08, 1] }}
    transition={{ duration, delay, repeat: Infinity, ease: "easeInOut" }}
  >
    {emoji}
  </motion.div>
);

const PwInput = ({
  icon: Icon,
  label,
  name,
  type,
  value,
  onChange,
  onFocus,
  onBlur,
  focused,
  error,
}) => (
  <div>
    <label
      style={{
        display: "block",
        fontSize: "0.67rem",
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: error ? "#ef4444" : focused ? "#0284c7" : "#64748b",
        marginBottom: 7,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        transition: "color 0.25s",
      }}
    >
      {label}
    </label>
    <div style={{ position: "relative" }}>
      <div
        style={{
          position: "absolute",
          left: 15,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 1,
          color: error ? "#f87171" : focused ? "#0ea5e9" : "#94a3b8",
          display: "flex",
          alignItems: "center",
          transition: "color 0.25s",
        }}
      >
        <Icon size={14} />
      </div>
      <motion.input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        required
        placeholder={label}
        animate={{
          boxShadow: error
            ? "0 0 0 3px rgba(239,68,68,0.15)"
            : focused
            ? "0 0 0 3px rgba(14,165,233,0.18), 0 4px 20px rgba(14,165,233,0.12)"
            : "0 1px 4px rgba(0,0,0,0.05)",
        }}
        style={{
          width: "100%",
          padding: "14px 48px 14px 42px",
          borderRadius: 12,
          border: error
            ? "2px solid #f87171"
            : focused
            ? "2px solid #0ea5e9"
            : "2px solid #e2e8f0",
          background: error
            ? "rgba(254,242,242,0.8)"
            : focused
            ? "rgba(240,249,255,0.9)"
            : "#f8fafc",
          fontSize: "0.9rem",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          color: "#0f172a",
          outline: "none",
          transition: "border-color 0.25s, background 0.25s",
          boxSizing: "border-box",
        }}
      />
    </div>
    <AnimatePresence>
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          style={{
            color: "#ef4444",
            fontSize: "0.72rem",
            marginTop: 5,
            fontWeight: 600,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {error}
        </motion.p>
      )}
    </AnimatePresence>
  </div>
);

const PasswordStrength = ({ password }) => {
  const strength = !password
    ? 0
    : password.length < 4
    ? 1
    : password.length < 7
    ? 2
    : /[A-Z]/.test(password) && /[0-9]/.test(password)
    ? 4
    : 3;
  const labels = ["", "Weak", "Fair", "Good", "Strong"];
  const colors = ["#e2e8f0", "#ef4444", "#f59e0b", "#3b82f6", "#10b981"];
  return password ? (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
        {[1, 2, 3, 4].map((i) => (
          <motion.div
            key={i}
            animate={{
              background: i <= strength ? colors[strength] : "#e2e8f0",
            }}
            transition={{ duration: 0.3 }}
            style={{ flex: 1, height: 3, borderRadius: 4 }}
          />
        ))}
      </div>
      <p
        style={{
          fontSize: "0.7rem",
          color: colors[strength],
          fontWeight: 700,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}
      >
        {labels[strength]}
      </p>
    </div>
  ) : null;
};

const ResetPasswordPage = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [focused, setFocused] = useState(null);
  const [countdown, setCountdown] = useState(5);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Reset Password - LiteUpper";
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    if (success) {
      const t = setInterval(
        () =>
          setCountdown((c) => {
            if (c <= 1) {
              clearInterval(t);
              navigate("/login");
              return 0;
            }
            return c - 1;
          }),
        1000
      );
      return () => clearInterval(t);
    }
  }, [success, navigate]);

  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");

  const handleReset = async () => {
    const err = {};
    if (!password) err.password = "Password required";
    else if (password.length < 6) err.password = "Min 6 characters";
    if (!confirmPassword) err.confirmPassword = "Confirm required";
    else if (password !== confirmPassword)
      err.confirmPassword = "Passwords do not match";
    setErrors(err);
    if (Object.keys(err).length > 0) return;

    setLoading(true);
    try {
      await axios.post(`${API_URL}/reset-password`, {
        new_password: password,
        confirm_password: confirmPassword,
        token,
      });
      setSuccess(true);
    } catch (error) {
      setErrors({
        general:
          error.response?.data?.message || "Failed to reset. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const E = [0.16, 1, 0.3, 1];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;0,900&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #f8fafc; overflow-x: hidden; }
        input:-webkit-autofill { -webkit-box-shadow: 0 0 0 100px #f8fafc inset !important; -webkit-text-fill-color: #0f172a !important; }
        ::-webkit-scrollbar { width: 5px; } ::-webkit-scrollbar-thumb { background: #bae6fd; border-radius: 8px; }
      `}</style>

      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          background: "#f8fafc",
        }}
      >
        {/* ══ LEFT PANEL – desktop only ══ */}
        {!isMobile && (
          <motion.div
            initial={{ opacity: 0, x: -90 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.1, ease: E }}
            style={{
              flex: "0 0 44%",
              minHeight: "100vh",
              background:
                "linear-gradient(150deg, #0369a1 0%, #0284c7 30%, #0ea5e9 65%, #38bdf8 100%)",
              position: "relative",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "48px 40px",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage:
                  "radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px)",
                backgroundSize: "30px 30px",
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: "25%",
                left: "18%",
                width: 300,
                height: 300,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 65%)",
                pointerEvents: "none",
              }}
            />

            <Ring
              size={380}
              color="rgba(255,255,255,0.07)"
              top="-80px"
              left="-80px"
              duration={24}
            />
            <Ring
              size={240}
              color="rgba(255,255,255,0.1)"
              top="50%"
              left="45%"
              duration={18}
              delay={2}
            />
            <Ring
              size={160}
              color="rgba(251,191,36,0.3)"
              bottom="-40px"
              right="-40px"
              duration={13}
              delay={3}
              dashed
            />
            <Ring
              size={110}
              color="rgba(255,255,255,0.16)"
              top="6%"
              left="60%"
              duration={9}
              delay={1}
            />
            <Pulse style={{ top: "18%", left: "10%" }} delay={0} />
            <Pulse style={{ top: "62%", left: "85%" }} delay={1} />
            <Pulse
              style={{ top: "82%", left: "25%" }}
              delay={2}
              color="rgba(251,191,36,0.8)"
            />
            <Pulse style={{ top: "10%", left: "80%" }} delay={3} />

            <FloatingIcon
              emoji="🔐"
              style={{ top: "7%", left: "7%" }}
              delay={0}
              duration={5.5}
            />
            <FloatingIcon
              emoji="🛡️"
              style={{ top: "10%", right: "8%" }}
              delay={0.9}
              duration={6.5}
            />
            <FloatingIcon
              emoji="🔑"
              style={{ top: "50%", left: "3%" }}
              delay={1.7}
              duration={5}
            />
            <FloatingIcon
              emoji="✅"
              style={{ bottom: "22%", right: "5%" }}
              delay={0.5}
              duration={7}
            />
            <FloatingIcon
              emoji="🔒"
              style={{ bottom: "8%", left: "13%" }}
              delay={2.1}
              duration={6.2}
            />

            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.8, duration: 1.4, ease: E }}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 5,
                background:
                  "linear-gradient(90deg,#f59e0b,#fbbf24,#fde68a,#fbbf24,#f59e0b)",
                transformOrigin: "left",
              }}
            />

            <div
              style={{
                position: "relative",
                zIndex: 3,
                textAlign: "center",
                maxWidth: 360,
              }}
            >
              <motion.div
                initial={{ scale: 0, rotate: -30 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{
                  delay: 0.4,
                  duration: 1,
                  type: "spring",
                  stiffness: 140,
                }}
              >
                <Link to="/" style={{ display: "inline-block" }}>
                  <motion.div
                    whileHover={{ scale: 1.08, rotate: 3 }}
                    transition={{ type: "spring", stiffness: 250 }}
                    style={{
                      width: 96,
                      height: 96,
                      borderRadius: 28,
                      background: "rgba(255,255,255,0.18)",
                      backdropFilter: "blur(20px)",
                      border: "2px solid rgba(255,255,255,0.35)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 28px",
                      boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
                      cursor: "pointer",
                    }}
                  >
                    <img
                      src={liteIcon}
                      alt="LiteUpper"
                      style={{
                        width: 60,
                        height: 60,
                        objectFit: "contain",
                        filter: "brightness(0) invert(1)",
                      }}
                    />
                  </motion.div>
                </Link>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 32 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.9, ease: E }}
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "clamp(28px, 3.2vw, 44px)",
                  fontWeight: 900,
                  color: "#fff",
                  lineHeight: 1.12,
                  marginBottom: 16,
                  letterSpacing: "-0.5px",
                  textShadow: "0 2px 20px rgba(0,0,0,0.12)",
                }}
              >
                Secure Your
                <span
                  style={{
                    display: "block",
                    background:
                      "linear-gradient(135deg, #fde68a, #fbbf24, #f59e0b)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Account
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.8, ease: E }}
                style={{
                  color: "rgba(255,255,255,0.84)",
                  fontSize: "0.98rem",
                  lineHeight: 1.75,
                  marginBottom: 32,
                  fontWeight: 500,
                }}
              >
                Create a strong new password to protect your LiteUpper account
                and keep your progress safe.
              </motion.p>

              {/* Security tips */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.88 }}
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                {[
                  { emoji: "🔡", text: "Use uppercase & lowercase letters" },
                  { emoji: "🔢", text: "Include numbers and symbols" },
                  { emoji: "📏", text: "Minimum 8 characters long" },
                  { emoji: "🚫", text: "Avoid common passwords" },
                ].map((t) => (
                  <motion.div
                    key={t.text}
                    whileHover={{ x: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      background: "rgba(255,255,255,0.12)",
                      backdropFilter: "blur(12px)",
                      border: "1px solid rgba(255,255,255,0.2)",
                      borderRadius: 10,
                      padding: "9px 14px",
                    }}
                  >
                    <span style={{ fontSize: 16 }}>{t.emoji}</span>
                    <span
                      style={{
                        color: "rgba(255,255,255,0.88)",
                        fontSize: "0.82rem",
                        fontWeight: 600,
                      }}
                    >
                      {t.text}
                    </span>
                  </motion.div>
                ))}
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 }}
              style={{
                position: "absolute",
                bottom: 32,
                left: 40,
                right: 40,
                borderLeft: "3px solid #fbbf24",
                paddingLeft: 16,
              }}
            >
              <p
                style={{
                  color: "rgba(255,255,255,0.65)",
                  fontSize: "0.78rem",
                  fontStyle: "italic",
                  fontFamily: "'Playfair Display', serif",
                  lineHeight: 1.6,
                }}
              >
                "Security is not a product, but a process."
              </p>
              <p
                style={{
                  color: "rgba(255,255,255,0.42)",
                  fontSize: "0.68rem",
                  marginTop: 5,
                  fontWeight: 700,
                }}
              >
                — Bruce Schneier
              </p>
            </motion.div>
          </motion.div>
        )}

        {/* ══ RIGHT PANEL ══ */}
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? "70px 16px 32px" : "40px 24px",
            position: "relative",
            background: "#f8fafc",
            minHeight: "100vh",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundImage:
                "radial-gradient(circle at 85% 15%, rgba(14,165,233,0.07) 0%, transparent 45%), radial-gradient(circle at 10% 85%, rgba(251,191,36,0.05) 0%, transparent 40%)",
              pointerEvents: "none",
              zIndex: 0,
            }}
          />

          {/* Back */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            onClick={() => navigate(-1)}
            whileHover={{ x: -3 }}
            whileTap={{ scale: 0.92 }}
            style={{
              position: "fixed",
              top: 20,
              left: 20,
              zIndex: 100,
              display: "flex",
              alignItems: "center",
              gap: 7,
              background: "rgba(255,255,255,0.95)",
              border: "1.5px solid rgba(14,165,233,0.25)",
              borderRadius: 12,
              padding: "9px 16px",
              color: "#0369a1",
              fontSize: "0.8rem",
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              backdropFilter: "blur(12px)",
              boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            }}
          >
            <FaArrowLeft size={11} /> Back
          </motion.button>

          {/* Card */}
          <motion.div
            style={{
              width: "100%",
              maxWidth: 440,
              position: "relative",
              zIndex: 1,
            }}
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, ease: E, delay: 0.15 }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: isMobile ? 20 : 26,
                boxShadow:
                  "0 32px 100px rgba(3,105,161,0.12), 0 4px 32px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.9)",
                border: "1px solid rgba(14,165,233,0.1)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: 4,
                  background:
                    "linear-gradient(90deg, #0369a1, #0284c7, #38bdf8, #fbbf24)",
                }}
              />

              <div
                style={{
                  padding: isMobile ? "28px 22px 28px" : "40px 40px 36px",
                }}
              >
                {/* Logo */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.28, duration: 0.6, type: "spring" }}
                  style={{ textAlign: "center", marginBottom: 20 }}
                >
                  <Link to="/">
                    <motion.img
                      src={liteIcon}
                      alt="LiteUpper"
                      whileHover={{ scale: 1.1, rotate: 4 }}
                      transition={{ type: "spring", stiffness: 260 }}
                      style={{
                        height: isMobile ? 64 : 76,
                        width: isMobile ? 64 : 76,
                        objectFit: "contain",
                        filter: "drop-shadow(0 8px 20px rgba(14,165,233,0.32))",
                        cursor: "pointer",
                      }}
                    />
                  </Link>
                </motion.div>

                <AnimatePresence mode="wait">
                  {success ? (
                    /* ── Success state ── */
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      style={{ textAlign: "center", padding: "20px 0" }}
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{
                          delay: 0.1,
                          type: "spring",
                          stiffness: 200,
                        }}
                        style={{
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          background:
                            "linear-gradient(135deg, #10b981, #34d399)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto 24px",
                          boxShadow: "0 16px 48px rgba(16,185,129,0.35)",
                        }}
                      >
                        <FaCheckCircle size={36} color="#fff" />
                      </motion.div>
                      <motion.h2
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        style={{
                          fontFamily: "'Playfair Display', serif",
                          fontSize: "1.75rem",
                          fontWeight: 900,
                          color: "#0c1a2e",
                          marginBottom: 12,
                          letterSpacing: "-0.3px",
                        }}
                      >
                        Password Reset!
                      </motion.h2>
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        style={{
                          color: "#64748b",
                          fontSize: "0.9rem",
                          lineHeight: 1.6,
                          marginBottom: 24,
                        }}
                      >
                        Your password has been successfully updated. You'll be
                        redirected to login shortly.
                      </motion.p>
                      <div
                        style={{
                          background:
                            "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
                          border: "1px solid rgba(14,165,233,0.2)",
                          borderRadius: 12,
                          padding: "14px 20px",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "0.8rem",
                            color: "#0369a1",
                            fontWeight: 700,
                          }}
                        >
                          Redirecting in {countdown}s…
                        </p>
                        <motion.div
                          animate={{ width: `${(countdown / 5) * 100}%` }}
                          transition={{ duration: 0.8 }}
                          style={{
                            height: 3,
                            background:
                              "linear-gradient(90deg, #0284c7, #38bdf8)",
                            borderRadius: 4,
                            marginTop: 8,
                          }}
                        />
                      </div>
                      <motion.button
                        onClick={() => navigate("/login")}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.97 }}
                        style={{
                          marginTop: 20,
                          width: "100%",
                          padding: "14px",
                          borderRadius: 12,
                          border: "none",
                          background:
                            "linear-gradient(135deg, #0369a1, #0284c7, #0ea5e9)",
                          color: "#fff",
                          fontSize: "0.95rem",
                          fontWeight: 800,
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          cursor: "pointer",
                          boxShadow: "0 10px 28px rgba(2,132,199,0.38)",
                        }}
                      >
                        Go to Login →
                      </motion.button>
                    </motion.div>
                  ) : (
                    /* ── Form state ── */
                    <motion.div
                      key="form"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.38 }}
                        style={{ textAlign: "center", marginBottom: 28 }}
                      >
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 7,
                            background:
                              "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
                            border: "1px solid rgba(14,165,233,0.22)",
                            borderRadius: 100,
                            padding: "7px 16px",
                            marginBottom: 14,
                          }}
                        >
                          <span style={{ fontSize: 14 }}>🔐</span>
                          <span
                            style={{
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              color: "#0369a1",
                              letterSpacing: "0.08em",
                              textTransform: "uppercase",
                            }}
                          >
                            Password Reset
                          </span>
                        </div>
                        <h2
                          style={{
                            fontFamily: "'Playfair Display', serif",
                            fontSize: isMobile ? "1.55rem" : "1.8rem",
                            fontWeight: 900,
                            color: "#0c1a2e",
                            letterSpacing: "-0.4px",
                            lineHeight: 1.2,
                            marginBottom: 8,
                          }}
                        >
                          Create New Password
                        </h2>
                        <p
                          style={{
                            color: "#64748b",
                            fontSize: "0.875rem",
                            fontWeight: 500,
                          }}
                        >
                          Enter and confirm your new secure password
                        </p>
                      </motion.div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 18,
                        }}
                      >
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.48, duration: 0.6 }}
                        >
                          <div style={{ position: "relative" }}>
                            <PwInput
                              icon={FaLock}
                              label="New Password"
                              name="password"
                              type={showPw ? "text" : "password"}
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              focused={focused === "password"}
                              onFocus={() => setFocused("password")}
                              onBlur={() => setFocused(null)}
                              error={errors.password}
                            />
                            <motion.button
                              type="button"
                              onClick={() => setShowPw((p) => !p)}
                              whileHover={{ scale: 1.15 }}
                              whileTap={{ scale: 0.88 }}
                              style={{
                                position: "absolute",
                                right: 14,
                                top: "50%",
                                transform: "translateY(calc(-50% + 12px))",
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                color: showPw ? "#0ea5e9" : "#94a3b8",
                                display: "flex",
                                padding: 4,
                                zIndex: 2,
                              }}
                            >
                              {showPw ? (
                                <FaEyeSlash size={14} />
                              ) : (
                                <FaEye size={14} />
                              )}
                            </motion.button>
                          </div>
                          <PasswordStrength password={password} />
                        </motion.div>

                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.58, duration: 0.6 }}
                        >
                          <div style={{ position: "relative" }}>
                            <PwInput
                              icon={FaLock}
                              label="Confirm New Password"
                              name="confirmPassword"
                              type={showConfirm ? "text" : "password"}
                              value={confirmPassword}
                              onChange={(e) =>
                                setConfirmPassword(e.target.value)
                              }
                              focused={focused === "confirmPassword"}
                              onFocus={() => setFocused("confirmPassword")}
                              onBlur={() => setFocused(null)}
                              error={errors.confirmPassword}
                            />
                            <motion.button
                              type="button"
                              onClick={() => setShowConfirm((p) => !p)}
                              whileHover={{ scale: 1.15 }}
                              whileTap={{ scale: 0.88 }}
                              style={{
                                position: "absolute",
                                right: 14,
                                top: "50%",
                                transform: "translateY(calc(-50% + 12px))",
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                color: showConfirm ? "#0ea5e9" : "#94a3b8",
                                display: "flex",
                                padding: 4,
                                zIndex: 2,
                              }}
                            >
                              {showConfirm ? (
                                <FaEyeSlash size={14} />
                              ) : (
                                <FaEye size={14} />
                              )}
                            </motion.button>
                          </div>
                          {/* Match indicator */}
                          <AnimatePresence>
                            {confirmPassword && password && (
                              <motion.p
                                initial={{ opacity: 0, y: -4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                style={{
                                  fontSize: "0.72rem",
                                  marginTop: 5,
                                  fontWeight: 700,
                                  color:
                                    password === confirmPassword
                                      ? "#10b981"
                                      : "#f59e0b",
                                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                                }}
                              >
                                {password === confirmPassword
                                  ? "✓ Passwords match"
                                  : "⚠ Passwords don't match yet"}
                              </motion.p>
                            )}
                          </AnimatePresence>
                        </motion.div>

                        <AnimatePresence>
                          {errors.general && (
                            <motion.div
                              initial={{ opacity: 0, y: -8 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0 }}
                              style={{
                                background: "#fef2f2",
                                border: "1px solid #fecaca",
                                borderRadius: 10,
                                padding: "12px 16px",
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                              }}
                            >
                              <span style={{ fontSize: 16 }}>⚠️</span>
                              <p
                                style={{
                                  color: "#ef4444",
                                  fontSize: "0.82rem",
                                  fontWeight: 600,
                                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                                }}
                              >
                                {errors.general}
                              </p>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        <motion.div
                          initial={{ opacity: 0, y: 14 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.68 }}
                        >
                          <motion.button
                            onClick={handleReset}
                            disabled={loading}
                            whileHover={
                              !loading
                                ? {
                                    scale: 1.02,
                                    boxShadow:
                                      "0 20px 50px rgba(2,132,199,0.5)",
                                  }
                                : {}
                            }
                            whileTap={!loading ? { scale: 0.97 } : {}}
                            style={{
                              width: "100%",
                              padding: "15px 24px",
                              borderRadius: 14,
                              border: "none",
                              background: loading
                                ? "rgba(14,165,233,0.55)"
                                : "linear-gradient(135deg, #0369a1 0%, #0284c7 45%, #0ea5e9 100%)",
                              color: "#fff",
                              fontSize: "0.975rem",
                              fontWeight: 800,
                              fontFamily: "'Plus Jakarta Sans', sans-serif",
                              cursor: loading ? "not-allowed" : "pointer",
                              boxShadow: "0 10px 32px rgba(2,132,199,0.38)",
                              transition: "box-shadow 0.25s",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 10,
                              letterSpacing: "0.02em",
                            }}
                          >
                            <AnimatePresence mode="wait">
                              {loading ? (
                                <motion.span
                                  key="l"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                  }}
                                >
                                  <CircularProgress
                                    size={17}
                                    sx={{ color: "#fff" }}
                                  />{" "}
                                  Resetting…
                                </motion.span>
                              ) : (
                                <motion.span
                                  key="t"
                                  initial={{ opacity: 0, y: 8 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -8 }}
                                >
                                  Reset Password →
                                </motion.span>
                              )}
                            </AnimatePresence>
                          </motion.button>
                        </motion.div>

                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.8 }}
                          style={{ textAlign: "center" }}
                        >
                          <span
                            style={{ color: "#64748b", fontSize: "0.875rem" }}
                          >
                            Remember your password?{" "}
                          </span>
                          <motion.button
                            type="button"
                            onClick={() => navigate("/login")}
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.96 }}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              fontWeight: 800,
                              color: "#0284c7",
                              fontSize: "0.875rem",
                              fontFamily: "'Plus Jakarta Sans', sans-serif",
                              padding: 0,
                              textDecoration: "underline",
                              textDecorationColor: "rgba(2,132,199,0.3)",
                              textUnderlineOffset: 3,
                            }}
                          >
                            Back to login
                          </motion.button>
                        </motion.div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default ResetPasswordPage;
